import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '@/context/AuthContext';
import SuperAdminCompanySelector, { useSuperAdminCompany } from '@/components/SuperAdminCompanySelector';
import { 
  MessageCircle, 
  ArrowLeft, 
  CheckCircle, 
  XCircle, 
  Plus,
  Edit2,
  Trash2,
  Send,
  Eye,
  Clock,
  AlertCircle,
  ExternalLink,
  Copy,
  RefreshCw,
  Save,
  FileText,
  Loader2,
  Image,
  Video,
  File,
  MapPin,
  Phone,
  Link,
  MessageSquare,
  X,
  Info,
  Sparkles
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle,
  DialogFooter
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import {
  Card,
  CardContent,
} from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from 'sonner';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Header type options
const HEADER_TYPES = [
  { value: 'NONE', label: 'No Header', icon: null },
  { value: 'TEXT', label: 'Text', icon: FileText },
  { value: 'IMAGE', label: 'Image', icon: Image },
  { value: 'VIDEO', label: 'Video', icon: Video },
  { value: 'DOCUMENT', label: 'Document', icon: File },
  { value: 'LOCATION', label: 'Location', icon: MapPin },
];

// Button type options
const BUTTON_TYPES = [
  { value: 'QUICK_REPLY', label: 'Quick Reply', icon: MessageSquare, description: 'Customer taps to send preset reply' },
  { value: 'URL', label: 'Visit Website', icon: Link, description: 'Opens a URL when tapped' },
  { value: 'PHONE_NUMBER', label: 'Call Phone', icon: Phone, description: 'Initiates a phone call' },
  { value: 'COPY_CODE', label: 'Copy Code', icon: Copy, description: 'Copies a coupon/promo code to clipboard' },
  { value: 'OTP', label: 'OTP Button', icon: Copy, description: 'For authentication templates - auto-fill OTP' },
  { value: 'FLOW', label: 'Flow', icon: MessageSquare, description: 'Opens a WhatsApp Flow' },
];

// Template categories
const TEMPLATE_CATEGORIES = [
  { value: 'MARKETING', label: 'Marketing', description: 'Promotions, offers, product launches' },
  { value: 'UTILITY', label: 'Utility', description: 'Order updates, shipping, appointments' },
  { value: 'AUTHENTICATION', label: 'Authentication', description: 'OTP codes, verification' },
];

// Template types (for carousel support)
const TEMPLATE_TYPES = [
  { value: 'STANDARD', label: 'Standard', description: 'Single message template' },
  { value: 'CAROUSEL', label: 'Carousel', description: 'Multi-card scrollable template (up to 10 cards)' },
];

// Languages
const LANGUAGES = [
  { value: 'en', label: 'English' },
  { value: 'en_US', label: 'English (US)' },
  { value: 'en_GB', label: 'English (UK)' },
  { value: 'ar', label: 'Arabic' },
  { value: 'hi', label: 'Hindi' },
  { value: 'ur', label: 'Urdu' },
  { value: 'fr', label: 'French' },
  { value: 'es', label: 'Spanish' },
];

const WhatsAppIntegrationPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [activeTab, setActiveTab] = useState('settings');
  
  // Super Admin company selector - use reusable hook
  const { 
    selectedCompanyId, 
    setSelectedCompanyId, 
    needsCompanySelector,
    isReady 
  } = useSuperAdminCompany();
  
  // Settings state
  const [settings, setSettings] = useState({
    configured: false,
    phone_number_id: '',
    business_account_id: '',
    access_token: '',
    access_token_masked: '',
    webhook_verify_token: '',
    app_id: ''
  });
  
  // Templates state
  const [templates, setTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [showTemplateDialog, setShowTemplateDialog] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [templateForm, setTemplateForm] = useState({
    name: '',
    category: 'MARKETING',
    language: 'en',
    template_type: 'STANDARD',
    header_type: 'NONE',
    header_text: '',
    header_handle: '',
    body: '',
    body_examples: [],
    footer: '',
    buttons: [],
    add_security_recommendation: false,
    code_expiration_minutes: null,
    // Carousel specific
    carousel_cards: []
  });
  const [detectedVariables, setDetectedVariables] = useState([]);
  
  // Test message state
  const [showTestDialog, setShowTestDialog] = useState(false);
  const [testPhone, setTestPhone] = useState('');
  const [testTemplate, setTestTemplate] = useState('');
  const [testVariables, setTestVariables] = useState({});
  const [testHeaderImageUrl, setTestHeaderImageUrl] = useState('');
  const [testButtonParams, setTestButtonParams] = useState({});
  const [testMessageStatus, setTestMessageStatus] = useState(null); // null, 'sending', 'sent', 'delivered', 'read', 'failed'
  const [testMessageError, setTestMessageError] = useState('');
  const [selectedTestTemplate, setSelectedTestTemplate] = useState(null);
  
  // View template state
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [viewingTemplate, setViewingTemplate] = useState(null);
  
  // Test sending state
  const [sendingTest, setSendingTest] = useState(false);
  
  // Phone registration state
  const [showRegisterDialog, setShowRegisterDialog] = useState(false);
  const [registerPin, setRegisterPin] = useState('');
  const [registering, setRegistering] = useState(false);
  
  // Config verification state
  const [verifying, setVerifying] = useState(false);
  const [verificationResult, setVerificationResult] = useState(null);
  
  // Remove integration state
  const [showRemoveDialog, setShowRemoveDialog] = useState(false);
  const [removing, setRemoving] = useState(false);
  
  // Media upload state
  const [uploadingMedia, setUploadingMedia] = useState(false);
  const mediaInputRef = React.useRef(null);
  
  useEffect(() => {
    // Load settings when ready (either regular user or super admin with selected company)
    if (isReady) {
      loadSettings();
    } else {
      setLoading(false);
    }
  }, [isReady, selectedCompanyId]);
  
  useEffect(() => {
    if (settings.configured && activeTab === 'templates') {
      loadTemplates();
    }
  }, [settings.configured, activeTab]);
  
  const loadSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      let url = `${API_BASE_URL}/whatsapp/settings`;
      if (needsCompanySelector && selectedCompanyId) {
        url += `?company_id=${selectedCompanyId}`;
      }
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSettings(response.data);
    } catch (error) {
      console.error('Failed to load WhatsApp settings');
    } finally {
      setLoading(false);
    }
  };
  
  const loadTemplates = async () => {
    setLoadingTemplates(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/whatsapp/templates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTemplates(response.data.templates || []);
    } catch (error) {
      console.error('Failed to load templates');
      toast.error('Failed to load templates');
    } finally {
      setLoadingTemplates(false);
    }
  };
  
  const handleSaveSettings = async () => {
    if (!settings.phone_number_id || !settings.business_account_id || !settings.access_token) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    // Super admin without company_id must select a company
    if (needsCompanySelector && !selectedCompanyId) {
      toast.error('Please select a company first');
      return;
    }
    
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const payload = {
        phone_number_id: settings.phone_number_id,
        business_account_id: settings.business_account_id,
        access_token: settings.access_token,
        app_id: settings.app_id,
        webhook_verify_token: settings.webhook_verify_token || `wt_${Date.now()}`
      };
      
      // Add company_id for super admin without their own company_id
      if (needsCompanySelector && selectedCompanyId) {
        payload.company_id = selectedCompanyId;
      }
      
      await axios.put(`${API_BASE_URL}/whatsapp/settings`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('WhatsApp settings saved successfully');
      loadSettings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };
  
  const handleTestConnection = async () => {
    setTesting(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_BASE_URL}/whatsapp/test`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        toast.success(`Connection successful! Phone: ${response.data.phone_number}`);
      } else {
        toast.error(response.data.message || 'Connection test failed');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Connection test failed');
    } finally {
      setTesting(false);
    }
  };
  
  const handleRegisterPhone = async () => {
    if (!registerPin || registerPin.length !== 6 || !/^\d+$/.test(registerPin)) {
      toast.error('Please enter a valid 6-digit PIN');
      return;
    }
    
    setRegistering(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_BASE_URL}/whatsapp/register-phone`, 
        { pin: registerPin },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        toast.success('Phone number registered successfully! You can now send messages.');
        setShowRegisterDialog(false);
        setRegisterPin('');
        // Reload settings to update status
        loadSettings();
      } else {
        toast.error(response.data.message || 'Registration failed');
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Registration failed';
      toast.error(errorMsg);
      
      // Show specific help for common errors
      if (errorMsg.includes('133010')) {
        toast.error('Please complete OTP verification in Meta Business Suite first', { duration: 5000 });
      }
    } finally {
      setRegistering(false);
    }
  };
  
  const handleVerifyConfig = async () => {
    setVerifying(true);
    setVerificationResult(null);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_BASE_URL}/whatsapp/debug/verify-phone-number`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setVerificationResult(response.data);
      
      if (response.data.config_match === "OK - Phone Number ID matches WABA") {
        toast.success('Configuration verified! Phone Number ID is correct.');
      } else if (response.data.config_match?.includes("MISMATCH")) {
        toast.error('Configuration mismatch! Check the verification results below.', { duration: 5000 });
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Verification failed');
      setVerificationResult({ error: error.response?.data?.detail || 'Verification failed' });
    } finally {
      setVerifying(false);
    }
  };
  
  const handleRemoveIntegration = async () => {
    setRemoving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_BASE_URL}/whatsapp/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('WhatsApp integration removed successfully');
      setShowRemoveDialog(false);
      
      // Reset all settings
      setSettings({
        phone_number_id: '',
        business_account_id: '',
        access_token: '',
        app_id: '',
        webhook_verify_token: '',
        configured: false
      });
      setTemplates([]);
      setVerificationResult(null);
      
      // Reload settings
      loadSettings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove integration');
    } finally {
      setRemoving(false);
    }
  };

  const handleCreateTemplate = async () => {
    if (!templateForm.name || !templateForm.body) {
      toast.error('Template name and body are required');
      return;
    }
    
    // Validate template name (only lowercase letters, numbers, underscores)
    if (!/^[a-z0-9_]+$/.test(templateForm.name)) {
      toast.error('Template name must only contain lowercase letters, numbers, and underscores');
      return;
    }
    
    // Validate that all variables have sample values
    if (detectedVariables.length > 0 && templateForm.body_examples.length < detectedVariables.length) {
      toast.error('Please provide sample text for all variables to avoid rejection');
      return;
    }
    
    // Validate media header - must have a sample URL for approval
    if (['IMAGE', 'VIDEO', 'DOCUMENT'].includes(templateForm.header_type) && !templateForm.header_handle) {
      toast.error(`Please upload a sample ${templateForm.header_type.toLowerCase()} for Meta approval`);
      return;
    }
    
    // Validate buttons
    for (const btn of templateForm.buttons) {
      if (!btn.text || btn.text.trim().length === 0) {
        toast.error('All buttons must have text');
        return;
      }
      if (btn.text.length > 25) {
        toast.error(`Button text "${btn.text.slice(0, 15)}..." exceeds 25 character limit`);
        return;
      }
      if (btn.type === 'URL' && !btn.url) {
        toast.error(`URL is required for button "${btn.text}"`);
        return;
      }
      if (btn.type === 'PHONE_NUMBER' && !btn.phone_number) {
        toast.error(`Phone number is required for button "${btn.text}"`);
        return;
      }
    }
    
    // Count URL buttons - max 2 allowed
    const urlButtonCount = templateForm.buttons.filter(b => b.type === 'URL').length;
    if (urlButtonCount > 2) {
      toast.error('Templates can have maximum 2 URL buttons');
      return;
    }
    
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      if (editingTemplate && editingTemplate.id) {
        // EDIT path → routes through the backend sanitiser so any
        // pasted invisible chars / duplicate `{{N}}` get scrubbed
        // before submission to Meta. Re-approval is automatic.
        await axios.post(
          `${API_BASE_URL}/whatsapp/templates/${encodeURIComponent(editingTemplate.id)}/edit`,
          templateForm,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        toast.success('Template edit submitted to Meta — back in approval queue.');
      } else {
        await axios.post(`${API_BASE_URL}/whatsapp/templates`, templateForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Template submitted for approval');
      }
      setShowTemplateDialog(false);
      setEditingTemplate(null);
      resetTemplateForm();
      loadTemplates();
    } catch (error) {
      const errorMsg = error.response?.data?.detail
        || (editingTemplate ? 'Failed to edit template' : 'Failed to create template');
      toast.error(errorMsg);
      console.error('Template save error:', errorMsg);
    } finally {
      setSaving(false);
    }
  };
  
  const handleDeleteTemplate = async (templateName) => {
    if (!window.confirm('Are you sure you want to delete this template?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_BASE_URL}/whatsapp/templates/${templateName}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Template deleted');
      loadTemplates();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete template');
    }
  };
  
  // Parse template to detect required variables, header type, and buttons
  const parseTemplateRequirements = (template) => {
    if (!template) return { bodyParams: [], hasHeaderImage: false, hasHeaderVideo: false, hasHeaderDocument: false, urlButtons: [] };
    
    const components = template.components || [];
    let bodyParams = [];
    let hasHeaderImage = false;
    let hasHeaderVideo = false;
    let hasHeaderDocument = false;
    let urlButtons = [];
    let bodyText = '';
    
    for (const comp of components) {
      if (comp.type === 'HEADER') {
        if (comp.format === 'IMAGE') hasHeaderImage = true;
        if (comp.format === 'VIDEO') hasHeaderVideo = true;
        if (comp.format === 'DOCUMENT') hasHeaderDocument = true;
      } else if (comp.type === 'BODY') {
        bodyText = comp.text || '';
        // Find all {{n}} placeholders
        const matches = bodyText.match(/\{\{(\d+)\}\}/g);
        if (matches) {
          bodyParams = matches.map((m, idx) => ({
            index: idx + 1,
            placeholder: m,
            example: comp.example?.body_text?.[0]?.[idx] || ''
          }));
        }
      } else if (comp.type === 'BUTTONS') {
        const buttons = comp.buttons || [];
        buttons.forEach((btn, idx) => {
          if (btn.type === 'URL' && btn.url?.includes('{{')) {
            urlButtons.push({
              index: idx,
              text: btn.text,
              baseUrl: btn.url
            });
          }
        });
      }
    }
    
    return { bodyParams, hasHeaderImage, hasHeaderVideo, hasHeaderDocument, urlButtons, bodyText };
  };
  
  // Handle template selection in test dialog
  const handleTestTemplateChange = (templateName) => {
    setTestTemplate(templateName);
    const template = templates.find(t => t.name === templateName);
    setSelectedTestTemplate(template);
    
    // Reset variables
    setTestVariables({});
    setTestHeaderImageUrl('');
    setTestButtonParams({});
    setTestMessageStatus(null);
    setTestMessageError('');
  };
  
  // Generate preview text with variables replaced
  const generatePreviewText = () => {
    if (!selectedTestTemplate) return '';
    const { bodyText } = parseTemplateRequirements(selectedTestTemplate);
    let preview = bodyText;
    
    Object.entries(testVariables).forEach(([key, value]) => {
      preview = preview.replace(`{{${key}}}`, value || `[Variable ${key}]`);
    });
    
    return preview;
  };
  
  const handleSendTestMessage = async () => {
    if (!testPhone || !testTemplate) {
      toast.error('Please enter phone number and select template');
      return;
    }
    
    const requirements = parseTemplateRequirements(selectedTestTemplate);
    
    // Validate header image if required
    if ((requirements.hasHeaderImage || requirements.hasHeaderVideo || requirements.hasHeaderDocument) && !testHeaderImageUrl) {
      toast.error('Please provide header media URL');
      return;
    }
    
    setSendingTest(true);
    setTestMessageStatus('sending');
    setTestMessageError('');
    
    try {
      const token = localStorage.getItem('token');
      
      // Build body parameters array in order
      const bodyParams = requirements.bodyParams.map((p, idx) => testVariables[idx + 1] || '');
      
      // Build button parameters
      const buttonParams = Object.entries(testButtonParams).map(([idx, value]) => ({
        index: parseInt(idx),
        value: value
      }));
      
      const response = await axios.post(`${API_BASE_URL}/whatsapp/send-test`, {
        phone: testPhone,
        template_name: testTemplate,
        body_params: bodyParams.length > 0 ? bodyParams : undefined,
        header_image_url: testHeaderImageUrl || undefined,
        button_params: buttonParams.length > 0 ? buttonParams : undefined
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setTestMessageStatus('sent');
      toast.success('Test message sent successfully!');
      
      // Poll for delivery status (optional enhancement)
      if (response.data?.message_id) {
        // Could implement status polling here
      }
      
    } catch (error) {
      setTestMessageStatus('failed');
      setTestMessageError(error.response?.data?.detail || 'Failed to send test message');
      toast.error(error.response?.data?.detail || 'Failed to send test message');
    } finally {
      setSendingTest(false);
    }
  };
  
  const resetTestDialog = () => {
    setTestPhone('');
    setTestTemplate('');
    setTestVariables({});
    setTestHeaderImageUrl('');
    setTestButtonParams({});
    setTestMessageStatus(null);
    setTestMessageError('');
    setSelectedTestTemplate(null);
  };
  
  const resetTemplateForm = () => {
    setTemplateForm({
      name: '',
      category: 'MARKETING',
      language: 'en',
      header_type: 'NONE',
      header_text: '',
      header_handle: '',
      body: '',
      body_examples: [],
      footer: '',
      buttons: [],
      add_security_recommendation: false,
      code_expiration_minutes: null
    });
    setEditingTemplate(null);
    setDetectedVariables([]);
  };
  
  // Detect variables in body text
  const handleBodyChange = (value) => {
    setTemplateForm({ ...templateForm, body: value });
    
    // Find all {{n}} patterns
    const matches = value.match(/\{\{(\d+)\}\}/g) || [];
    const uniqueVars = [...new Set(matches)].sort();
    setDetectedVariables(uniqueVars);
    
    // Ensure body_examples array has correct length
    if (uniqueVars.length !== templateForm.body_examples.length) {
      const newExamples = [...templateForm.body_examples];
      while (newExamples.length < uniqueVars.length) {
        newExamples.push('');
      }
      setTemplateForm(prev => ({ ...prev, body_examples: newExamples.slice(0, uniqueVars.length) }));
    }
  };
  
  // Update example value
  const handleExampleChange = (index, value) => {
    const newExamples = [...templateForm.body_examples];
    newExamples[index] = value;
    setTemplateForm({ ...templateForm, body_examples: newExamples });
  };
  
  // Add button
  const addButton = () => {
    if (templateForm.buttons.length >= 3) {
      toast.error('Maximum 3 buttons allowed');
      return;
    }
    setTemplateForm({
      ...templateForm,
      buttons: [...templateForm.buttons, { type: 'QUICK_REPLY', text: '', url_type: 'STATIC' }]
    });
  };
  
  // Update button
  const updateButton = (index, field, value) => {
    const newButtons = [...templateForm.buttons];
    newButtons[index] = { ...newButtons[index], [field]: value };
    setTemplateForm({ ...templateForm, buttons: newButtons });
  };
  
  // Remove button
  const removeButton = (index) => {
    const newButtons = templateForm.buttons.filter((_, i) => i !== index);
    setTemplateForm({ ...templateForm, buttons: newButtons });
  };


  // Handle media file upload for template header
  const handleMediaUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type based on header_type
    const allowedTypes = {
      'IMAGE': ['image/jpeg', 'image/png', 'image/webp'],
      'VIDEO': ['video/mp4', 'video/3gpp'],
      'DOCUMENT': ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document']
    };

    const allowed = allowedTypes[templateForm.header_type] || [];
    if (!allowed.includes(file.type)) {
      toast.error(`Invalid file type. Allowed: ${allowed.join(', ')}`);
      return;
    }

    // Validate file size (max 5MB for images, 16MB for videos, 100MB for documents)
    const maxSizes = {
      'IMAGE': 5 * 1024 * 1024,
      'VIDEO': 16 * 1024 * 1024,
      'DOCUMENT': 100 * 1024 * 1024
    };
    const maxSize = maxSizes[templateForm.header_type] || 5 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error(`File too large. Max size: ${Math.round(maxSize / 1024 / 1024)}MB`);
      return;
    }

    setUploadingMedia(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);
      formData.append('purpose', 'whatsapp_template');

      const response = await axios.post(
        `${API_BASE_URL}/storage/upload`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      if (response.data.url) {
        setTemplateForm({ ...templateForm, header_handle: response.data.url });
        toast.success('Media uploaded successfully');
      } else {
        throw new Error('No URL returned');
      }
    } catch (error) {
      console.error('Upload failed:', error);
      toast.error('Failed to upload media');
    } finally {
      setUploadingMedia(false);
      // Reset file input
      if (mediaInputRef.current) {
        mediaInputRef.current.value = '';
      }
    }
  };

  
  const getStatusBadge = (status) => {
    const statusMap = {
      'APPROVED': { color: 'bg-green-500/20 text-green-400', icon: CheckCircle },
      'PENDING': { color: 'bg-yellow-500/20 text-yellow-400', icon: Clock },
      'REJECTED': { color: 'bg-red-500/20 text-red-400', icon: XCircle },
    };
    const config = statusMap[status] || statusMap['PENDING'];
    const Icon = config.icon;
    return (
      <Badge className={`${config.color} border-0`}>
        <Icon className="h-3 w-3 mr-1" />
        {status}
      </Badge>
    );
  };
  
  // Use the production domain for webhook URL (from backend APP_DOMAIN)
  const getProductionWebhookUrl = () => {
    // Production webhook should always point to opswarex.app
    return 'https://opswarex.app/api/whatsapp/webhook';
  };
  
  const copyWebhookUrl = () => {
    const webhookUrl = getProductionWebhookUrl();
    navigator.clipboard.writeText(webhookUrl);
    toast.success('Webhook URL copied to clipboard');
  };
  
  const isConnected = settings.configured;
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-wt-gold"></div>
      </div>
    );
  }
  
  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          onClick={() => navigate('/integrations')}
          className="text-wt-text-muted hover:text-wt-text-primary"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Integrations
        </Button>
      </div>
      
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-gradient-to-br from-green-500 to-green-600 rounded-xl">
            <MessageCircle className="h-8 w-8 text-white" />
          </div>
          <div>
            <h1 className="text-3xl font-bold font-playfair text-wt-text-primary">WhatsApp Business</h1>
            <p className="text-wt-text-muted mt-1">Send messages and manage templates via Meta Cloud API</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Badge 
            className={isConnected 
              ? 'bg-green-500/20 text-green-400 border-green-500/30 text-sm px-3 py-1' 
              : 'bg-gray-500/20 text-gray-400 border-gray-500/30 text-sm px-3 py-1'
            }
          >
            {isConnected ? (
              <><CheckCircle className="h-4 w-4 mr-1" /> Connected</>
            ) : (
              <><XCircle className="h-4 w-4 mr-1" /> Not Connected</>
            )}
          </Badge>
          {isConnected && (
            <Button
              onClick={() => setShowTestDialog(true)}
              className="bg-green-600 hover:bg-green-700 text-white"
            >
              <Send className="h-4 w-4 mr-2" />
              Send Test
            </Button>
          )}
        </div>
      </div>
      
      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="bg-wt-surface border border-wt-border">
          <TabsTrigger value="settings" className="data-[state=active]:bg-wt-gold data-[state=active]:text-black">
            Settings
          </TabsTrigger>
          <TabsTrigger value="templates" className="data-[state=active]:bg-wt-gold data-[state=active]:text-black" disabled={!isConnected}>
            Templates
          </TabsTrigger>
          <TabsTrigger value="setup" className="data-[state=active]:bg-wt-gold data-[state=active]:text-black">
            Setup Guide
          </TabsTrigger>
        </TabsList>
        
        {/* Settings Tab */}
        <TabsContent value="settings" className="mt-6">
          {/* Company Selector for Super Admin */}
          <SuperAdminCompanySelector
            selectedCompanyId={selectedCompanyId}
            onCompanyChange={setSelectedCompanyId}
            integrationName="WhatsApp"
          />
          
          {/* Show message if super admin without company_id hasn't selected a company */}
          {needsCompanySelector && !selectedCompanyId ? (
            <div className="text-center py-12 text-wt-text-muted">
              <MessageCircle className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>Please select a company above to configure WhatsApp integration.</p>
            </div>
          ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* API Configuration */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-wt-surface border border-wt-border rounded-xl p-6"
            >
              <h2 className="text-lg font-semibold text-wt-text-primary mb-4">API Configuration</h2>
              
              <div className="space-y-4">
                <div>
                  <Label className="text-wt-text-primary">Phone Number ID *</Label>
                  <Input
                    value={settings.phone_number_id}
                    onChange={(e) => setSettings({ ...settings, phone_number_id: e.target.value })}
                    placeholder="e.g., 1234567890123456"
                    className="bg-wt-paper border-wt-border text-wt-text-primary mt-1"
                  />
                </div>
                
                <div>
                  <Label className="text-wt-text-primary">WhatsApp Business Account ID *</Label>
                  <Input
                    value={settings.business_account_id}
                    onChange={(e) => setSettings({ ...settings, business_account_id: e.target.value })}
                    placeholder="e.g., 1234567890123456"
                    className="bg-wt-paper border-wt-border text-wt-text-primary mt-1"
                  />
                </div>
                
                <div>
                  <Label className="text-wt-text-primary">Access Token *</Label>
                  <Input
                    type="password"
                    value={settings.access_token}
                    onChange={(e) => setSettings({ ...settings, access_token: e.target.value })}
                    placeholder={settings.configured ? settings.access_token_masked : "Enter your access token"}
                    className="bg-wt-paper border-wt-border text-wt-text-primary mt-1"
                  />
                  <p className="text-xs text-wt-text-muted mt-1">
                    Get from Meta Business Suite → WhatsApp → API Setup
                  </p>
                </div>
                
                <div>
                  <Label className="text-wt-text-primary">App ID (Optional)</Label>
                  <Input
                    value={settings.app_id}
                    onChange={(e) => setSettings({ ...settings, app_id: e.target.value })}
                    placeholder="e.g., 1234567890"
                    className="bg-wt-paper border-wt-border text-wt-text-primary mt-1"
                  />
                </div>
                
                <div className="flex flex-wrap gap-2 pt-4">
                  <Button
                    onClick={handleSaveSettings}
                    disabled={saving}
                    className="bg-wt-gold hover:bg-wt-gold-dark text-black"
                  >
                    <Save className="h-4 w-4 mr-2" />
                    {saving ? 'Saving...' : 'Save Settings'}
                  </Button>
                  
                  {isConnected && (
                    <Button
                      onClick={handleTestConnection}
                      disabled={testing}
                      variant="outline"
                      className="border-wt-border text-wt-text-primary"
                    >
                      {testing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
                      Test Connection
                    </Button>
                  )}
                  {isConnected && (
                    <Button
                      onClick={handleVerifyConfig}
                      disabled={verifying}
                      variant="outline"
                      className="border-wt-border text-wt-text-primary"
                    >
                      {verifying ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle className="h-4 w-4 mr-2" />}
                      Verify Config
                    </Button>
                  )}
                </div>
                
                {/* Second row of buttons */}
                {isConnected && (
                  <div className="flex flex-wrap gap-2 pt-2">
                    <Button
                      onClick={() => setShowRegisterDialog(true)}
                      variant="outline"
                      className="border-wt-border text-wt-text-primary"
                    >
                      <Phone className="h-4 w-4 mr-2" />
                      Register Phone
                    </Button>
                    <Button
                      onClick={() => setShowRemoveDialog(true)}
                      variant="outline"
                      className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Remove Integration
                    </Button>
                  </div>
                )}
                
                {/* Verification Result */}
                {verificationResult && (
                  <div className={`mt-4 p-4 rounded-lg border ${verificationResult.config_match?.includes("OK") ? 'bg-green-500/10 border-green-500/30' : 'bg-red-500/10 border-red-500/30'}`}
                       data-testid="verify-config-result">
                    <h4 className="font-semibold text-wt-text-primary mb-2">Verification Result</h4>
                    
                    {verificationResult.config_match && (
                      <p className={`text-sm mb-2 ${verificationResult.config_match.includes("OK") ? 'text-green-400' : 'text-red-400'}`}>
                        <strong>Status:</strong> {verificationResult.config_match}
                      </p>
                    )}
                    
                    {verificationResult.api_verification?.phone_number_details?.display_phone_number && (
                      <p className="text-sm text-wt-text-secondary">
                        <strong>Phone:</strong> {verificationResult.api_verification.phone_number_details.display_phone_number}
                      </p>
                    )}

                    {/* If Meta rejected the Phone Number lookup, surface it cleanly
                        instead of crashing on undefined fields. */}
                    {verificationResult.api_verification?.phone_number_details &&
                     ['FAILED', 'ERROR'].includes(verificationResult.api_verification.phone_number_details.status) && (
                      <p className="text-sm text-red-400 mb-2">
                        <strong>Phone Number lookup failed:</strong>{' '}
                        {(() => {
                          const err = verificationResult.api_verification.phone_number_details.error;
                          if (!err) return 'Unknown error';
                          if (typeof err === 'string') return err;
                          return err?.error?.message || JSON.stringify(err);
                        })()}
                      </p>
                    )}
                    
                    {verificationResult.suggestion && (
                      <div className="mt-2 p-2 bg-yellow-500/20 rounded">
                        <p className="text-sm text-yellow-400">
                          <strong>Suggestion:</strong> {verificationResult.suggestion}
                        </p>
                      </div>
                    )}
                    
                    {/* Defensive render: backend may return either an ARRAY of
                        phone numbers (success) or an OBJECT {status, error}
                        (failure). Calling `.map` on the object crashes React
                        — guard it with `Array.isArray`. */}
                    {Array.isArray(verificationResult.api_verification?.waba_phone_numbers) && verificationResult.api_verification.waba_phone_numbers.length > 0 && (
                      <div className="mt-2">
                        <p className="text-sm text-wt-text-muted mb-1">Available Phone Numbers in WABA:</p>
                        {verificationResult.api_verification.waba_phone_numbers.map((pn, idx) => (
                          <div key={idx} className="text-xs bg-wt-bg p-2 rounded mb-1">
                            <span className="text-wt-gold font-mono">{pn.id}</span>
                            <span className="text-wt-text-muted ml-2">{pn.display_phone_number}</span>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* WABA phone-numbers lookup failed — show the underlying
                        Meta error so the user can act on it (most common
                        cause: the access token lacks the
                        `whatsapp_business_management` permission). */}
                    {verificationResult.api_verification?.waba_phone_numbers &&
                     !Array.isArray(verificationResult.api_verification.waba_phone_numbers) && (
                      <div className="mt-2 p-2 bg-amber-500/10 border border-amber-500/30 rounded">
                        <p className="text-sm text-amber-300">
                          <strong>WABA phone-number lookup failed.</strong>{' '}
                          {(() => {
                            const err = verificationResult.api_verification.waba_phone_numbers.error;
                            if (!err) return 'Meta returned no details.';
                            if (typeof err === 'string') return err;
                            return err?.error?.message || JSON.stringify(err);
                          })()}
                        </p>
                        <p className="text-xs text-amber-300/80 mt-1">
                          Tip: this often means your Access Token lacks the
                          {' '}<code>whatsapp_business_management</code>{' '}
                          permission. Sending messages still works without it
                          — only the WABA listing on this page is affected.
                        </p>
                      </div>
                    )}
                    
                    {verificationResult.error && (
                      <p className="text-sm text-red-400">{verificationResult.error}</p>
                    )}
                  </div>
                )}
                
                {/* Help text for registration */}
                {isConnected && !verificationResult && (
                  <div className="mt-4 p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                    <p className="text-sm text-blue-400">
                      <strong>Getting "Account not registered" error?</strong><br />
                      1. First, complete OTP verification in Meta Business Suite<br />
                      2. Then click "Register Phone" and enter a 6-digit PIN<br />
                      3. This PIN will be used for two-factor authentication
                    </p>
                  </div>
                )}
              </div>
            </motion.div>
            
            {/* Webhook Configuration */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-wt-surface border border-wt-border rounded-xl p-6"
            >
              <h2 className="text-lg font-semibold text-wt-text-primary mb-4">Webhook Configuration</h2>
              
              <div className="space-y-4">
                <div>
                  <Label className="text-wt-text-primary">Webhook URL</Label>
                  <div className="flex gap-2 mt-1">
                    <Input
                      value={getProductionWebhookUrl()}
                      readOnly
                      className="bg-wt-paper border-wt-border text-wt-text-primary font-mono text-sm"
                    />
                    <Button
                      variant="outline"
                      onClick={copyWebhookUrl}
                      className="border-wt-border text-wt-text-primary"
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                
                <div>
                  <Label className="text-wt-text-primary">Verify Token</Label>
                  <div className="flex gap-2 mt-1">
                    <Input
                      value={settings.webhook_verify_token || 'Save settings to generate'}
                      readOnly
                      className="bg-wt-paper border-wt-border text-wt-text-primary font-mono text-sm"
                    />
                    <Button
                      variant="outline"
                      onClick={() => {
                        navigator.clipboard.writeText(settings.webhook_verify_token);
                        toast.success('Verify token copied');
                      }}
                      disabled={!settings.webhook_verify_token}
                      className="border-wt-border text-wt-text-primary"
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                
                <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-4 mt-4">
                  <p className="text-sm text-blue-400 font-medium mb-2">Webhook Setup</p>
                  <ol className="text-xs text-wt-text-muted space-y-1 list-decimal list-inside">
                    <li>Go to Meta App Dashboard → WhatsApp → Configuration</li>
                    <li>Click "Edit" on Webhook</li>
                    <li>Paste the Webhook URL and Verify Token</li>
                    <li>Subscribe to: messages, message_templates</li>
                  </ol>
                </div>
              </div>
            </motion.div>
          </div>
          )}
        </TabsContent>
        
        {/* Templates Tab */}
        <TabsContent value="templates" className="mt-6">
          <div className="mb-4 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg flex items-start gap-3">
            <Sparkles className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="text-sm font-medium text-amber-200">Templates have moved to Marketing &gt; Templates</div>
              <div className="text-xs text-amber-200/70 mt-0.5">Manage all your Email and WhatsApp templates in one place. This tab will be removed in a future update.</div>
            </div>
            <Button size="sm" onClick={() => navigate('/marketing/templates')} className="bg-amber-500 hover:bg-amber-600 text-black">
              Open Templates Hub
            </Button>
          </div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-wt-surface border border-wt-border rounded-xl p-6"
          >
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-wt-text-primary">Message Templates</h2>
              <div className="flex gap-2">
                <Button
                  onClick={loadTemplates}
                  disabled={loadingTemplates}
                  variant="outline"
                  className="border-wt-border text-wt-text-primary"
                >
                  <RefreshCw className={`h-4 w-4 mr-2 ${loadingTemplates ? 'animate-spin' : ''}`} />
                  Refresh
                </Button>
                <Button
                  onClick={() => {
                    resetTemplateForm();
                    setShowTemplateDialog(true);
                  }}
                  className="bg-wt-gold hover:bg-wt-gold-dark text-black"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Create Template
                </Button>
              </div>
            </div>
            
            {loadingTemplates ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-wt-gold" />
              </div>
            ) : templates.length === 0 ? (
              <div className="text-center py-12">
                <FileText className="h-12 w-12 text-wt-text-muted mx-auto mb-3" />
                <p className="text-wt-text-muted">No templates found</p>
                <p className="text-sm text-wt-text-muted mt-1">Create your first message template to get started</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="border-wt-border">
                    <TableHead className="text-wt-text-muted">Name</TableHead>
                    <TableHead className="text-wt-text-muted">Category</TableHead>
                    <TableHead className="text-wt-text-muted">Language</TableHead>
                    <TableHead className="text-wt-text-muted">Status</TableHead>
                    <TableHead className="text-wt-text-muted text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {templates.map((template) => (
                    <TableRow key={template.id || template.name} className="border-wt-border">
                      <TableCell className="text-wt-text-primary font-medium">{template.name}</TableCell>
                      <TableCell className="text-wt-text-muted">{template.category}</TableCell>
                      <TableCell className="text-wt-text-muted">{template.language}</TableCell>
                      <TableCell>{getStatusBadge(template.status)}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          {/* View Template */}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setViewingTemplate(template);
                              setShowViewDialog(true);
                            }}
                            className="text-wt-text-muted hover:text-wt-text-primary"
                            title="View Template"
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          {/* Edit Template (for rejected or draft) */}
                          {(template.status === 'REJECTED' || template.status === 'DRAFT') && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                // Pre-fill form with template data for editing
                                const body = template.components?.find(c => c.type === 'BODY')?.text || '';
                                const header = template.components?.find(c => c.type === 'HEADER');
                                const footer = template.components?.find(c => c.type === 'FOOTER')?.text || '';
                                const buttons = template.components?.find(c => c.type === 'BUTTONS')?.buttons || [];
                                
                                setTemplateForm({
                                  name: template.name,
                                  category: template.category,
                                  language: template.language,
                                  header_type: header?.format || 'NONE',
                                  header_text: header?.format === 'TEXT' ? header?.text : '',
                                  header_handle: '',
                                  body: body,
                                  body_examples: [],
                                  footer: footer,
                                  buttons: buttons.map(btn => ({
                                    type: btn.type,
                                    text: btn.text,
                                    url: btn.url,
                                    url_type: btn.url?.includes('{{') ? 'DYNAMIC' : 'STATIC',
                                    phone_number: btn.phone_number
                                  })),
                                  add_security_recommendation: false,
                                  code_expiration_minutes: null
                                });
                                setEditingTemplate(template);
                                setShowTemplateDialog(true);
                              }}
                              className="text-wt-gold hover:text-wt-gold-hover"
                              title="Edit & Resubmit"
                            >
                              <Edit2 className="h-4 w-4" />
                            </Button>
                          )}
                          {/* Send Test */}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setTestTemplate(template.name);
                              setShowTestDialog(true);
                            }}
                            disabled={template.status !== 'APPROVED'}
                            className="text-wt-text-muted hover:text-wt-text-primary"
                            title="Send Test"
                          >
                            <Send className="h-4 w-4" />
                          </Button>
                          {/* Delete */}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteTemplate(template.name)}
                            className="text-red-400 hover:text-red-300"
                            title="Delete"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </motion.div>
        </TabsContent>
        
        {/* Setup Guide Tab */}
        <TabsContent value="setup" className="mt-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-wt-surface border border-wt-border rounded-xl p-6"
          >
            <h2 className="text-lg font-semibold text-wt-text-primary mb-4">Setup Guide</h2>
            
            <div className="space-y-6">
              <div className="flex gap-4">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-wt-gold/20 flex items-center justify-center text-wt-gold font-semibold">1</div>
                <div>
                  <h3 className="text-wt-text-primary font-medium">Create Meta Business Account</h3>
                  <p className="text-sm text-wt-text-muted mt-1">
                    Go to <a href="https://business.facebook.com" target="_blank" rel="noopener noreferrer" className="text-wt-gold hover:underline">business.facebook.com</a> and create a business account if you don't have one.
                  </p>
                </div>
              </div>
              
              <div className="flex gap-4">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-wt-gold/20 flex items-center justify-center text-wt-gold font-semibold">2</div>
                <div>
                  <h3 className="text-wt-text-primary font-medium">Create Meta App</h3>
                  <p className="text-sm text-wt-text-muted mt-1">
                    Go to <a href="https://developers.facebook.com/apps" target="_blank" rel="noopener noreferrer" className="text-wt-gold hover:underline">developers.facebook.com/apps</a> → Create App → Select "Business" → Add WhatsApp product.
                  </p>
                </div>
              </div>
              
              <div className="flex gap-4">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-wt-gold/20 flex items-center justify-center text-wt-gold font-semibold">3</div>
                <div>
                  <h3 className="text-wt-text-primary font-medium">Get API Credentials</h3>
                  <p className="text-sm text-wt-text-muted mt-1">
                    In your app, go to WhatsApp → API Setup. Copy the Phone Number ID, Business Account ID, and generate a permanent Access Token.
                  </p>
                </div>
              </div>
              
              <div className="flex gap-4">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-wt-gold/20 flex items-center justify-center text-wt-gold font-semibold">4</div>
                <div>
                  <h3 className="text-wt-text-primary font-medium">Add Phone Number</h3>
                  <p className="text-sm text-wt-text-muted mt-1">
                    Register your business phone number in Meta Business Suite. You can use a test number for development or add your own number for production.
                  </p>
                </div>
              </div>
              
              <div className="flex gap-4">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-wt-gold/20 flex items-center justify-center text-wt-gold font-semibold">5</div>
                <div>
                  <h3 className="text-wt-text-primary font-medium">Configure Webhook</h3>
                  <p className="text-sm text-wt-text-muted mt-1">
                    In WhatsApp → Configuration, set up the webhook using the URL and verify token from this page. Subscribe to "messages" events.
                  </p>
                </div>
              </div>
              
              <div className="flex gap-4">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-green-500/20 flex items-center justify-center text-green-400 font-semibold">6</div>
                <div>
                  <h3 className="text-wt-text-primary font-medium">Create Message Templates</h3>
                  <p className="text-sm text-wt-text-muted mt-1">
                    Create message templates for notifications. Templates must be approved by Meta before use (usually takes a few minutes to 24 hours).
                  </p>
                </div>
              </div>
              
              <div className="mt-6 pt-4 border-t border-wt-border">
                <Button
                  variant="outline"
                  onClick={() => window.open('https://developers.facebook.com/docs/whatsapp/cloud-api/get-started', '_blank')}
                  className="border-wt-border text-wt-text-muted hover:text-wt-text-primary"
                >
                  <ExternalLink className="h-4 w-4 mr-2" />
                  View Official Documentation
                </Button>
              </div>
            </div>
          </motion.div>
        </TabsContent>
      </Tabs>
      
      {/* Create Template Dialog - Full Featured */}
      <Dialog open={showTemplateDialog} onOpenChange={(v) => { setShowTemplateDialog(v); if (!v) setEditingTemplate(null); }}>
        <DialogContent className="bg-wt-surface border-wt-border max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-wt-text-primary text-xl">
              {editingTemplate ? 'Edit Template' : 'Create Message Template'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Info Banner */}
            <div className="flex items-start gap-3 p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
              <Info className="h-5 w-5 text-blue-400 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-blue-300">
                <p className="font-medium mb-1">Important: Provide Sample Text for Variables</p>
                <p className="text-blue-400/80">Templates with variables (like {'{{1}}'}) require sample text to avoid rejection. Fill in the "Sample Values" section below for each variable.</p>
              </div>
            </div>
            
            {/* Basic Info */}
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label className="text-wt-text-primary">Template Name *</Label>
                <Input
                  value={templateForm.name}
                  onChange={(e) => setTemplateForm({ ...templateForm, name: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_') })}
                  placeholder="e.g., order_confirmation"
                  className="bg-wt-paper border-wt-border text-wt-text-primary mt-1.5"
                />
                <p className="text-xs text-wt-text-muted mt-1">Lowercase, underscores only</p>
              </div>
              
              <div>
                <Label className="text-wt-text-primary">Category *</Label>
                <Select
                  value={templateForm.category}
                  onValueChange={(value) => setTemplateForm({ ...templateForm, category: value })}
                >
                  <SelectTrigger className="bg-wt-paper border-wt-border text-wt-text-primary mt-1.5">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-wt-surface border-wt-border">
                    {TEMPLATE_CATEGORIES.map(cat => (
                      <SelectItem key={cat.value} value={cat.value} className="text-wt-text-primary">
                        <div>
                          <span className="font-medium">{cat.label}</span>
                          <span className="text-wt-text-muted text-xs ml-2">- {cat.description}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label className="text-wt-text-primary">Template Type</Label>
                <Select
                  value={templateForm.template_type}
                  onValueChange={(value) => setTemplateForm({ ...templateForm, template_type: value, carousel_cards: value === 'CAROUSEL' ? [{ header_handle: '', body: '', buttons: [] }] : [] })}
                >
                  <SelectTrigger className="bg-wt-paper border-wt-border text-wt-text-primary mt-1.5">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-wt-surface border-wt-border">
                    {TEMPLATE_TYPES.map(type => (
                      <SelectItem key={type.value} value={type.value} className="text-wt-text-primary">
                        <div>
                          <span className="font-medium">{type.label}</span>
                          <span className="text-wt-text-muted text-xs ml-2">- {type.description}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label className="text-wt-text-primary">Language</Label>
                <Select
                  value={templateForm.language}
                  onValueChange={(value) => setTemplateForm({ ...templateForm, language: value })}
                >
                  <SelectTrigger className="bg-wt-paper border-wt-border text-wt-text-primary mt-1.5">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-wt-surface border-wt-border">
                    {LANGUAGES.map(lang => (
                      <SelectItem key={lang.value} value={lang.value} className="text-wt-text-primary">
                        {lang.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {/* Header Section */}
            <div className="space-y-3">
              <Label className="text-wt-text-primary text-base font-medium">Header (Optional)</Label>
              <div className="flex flex-wrap gap-2">
                {HEADER_TYPES.map(type => {
                  const Icon = type.icon;
                  return (
                    <button
                      key={type.value}
                      onClick={() => setTemplateForm({ ...templateForm, header_type: type.value, header_text: '', header_handle: '' })}
                      className={`flex items-center gap-2 px-3 py-2 rounded-lg border transition-colors ${
                        templateForm.header_type === type.value
                          ? 'border-wt-gold bg-wt-gold/10 text-wt-gold'
                          : 'border-wt-border text-wt-text-muted hover:border-wt-gold/50'
                      }`}
                    >
                      {Icon && <Icon className="h-4 w-4" />}
                      <span className="text-sm">{type.label}</span>
                    </button>
                  );
                })}
              </div>
              
              {templateForm.header_type === 'TEXT' && (
                <Input
                  value={templateForm.header_text}
                  onChange={(e) => setTemplateForm({ ...templateForm, header_text: e.target.value })}
                  placeholder="Header text (max 60 characters)"
                  maxLength={60}
                  className="bg-wt-paper border-wt-border text-wt-text-primary"
                />
              )}
              
              {['IMAGE', 'VIDEO', 'DOCUMENT'].includes(templateForm.header_type) && (
                <div className="space-y-3">
                  {/* App ID Warning */}
                  {!settings.app_id && (
                    <div className="p-4 bg-orange-500/10 rounded-lg border border-orange-500/30">
                      <p className="text-sm text-orange-400">
                        <strong>⚠️ App ID Required:</strong> To create templates with media headers, you must configure your Meta App ID in the Settings tab.
                        Without it, the upload will fail.
                      </p>
                    </div>
                  )}
                  
                  <div className="p-4 bg-blue-500/10 rounded-lg border border-blue-500/30">
                    <p className="text-sm text-blue-400">
                      <strong>How it works:</strong> Upload your sample media below. We'll use Meta's Resumable Upload API 
                      to get the required header_handle for template approval.
                    </p>
                  </div>
                  
                  <div>
                    <Label className="text-wt-text-muted text-sm mb-2 block">
                      Sample {templateForm.header_type.toLowerCase()} (required for Meta approval)
                    </Label>
                    
                    {/* Upload Section */}
                    <div className="flex gap-3 items-start">
                      <div className="flex-1">
                        <div 
                          className={`border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-colors
                            ${templateForm.header_handle 
                              ? 'border-green-500/50 bg-green-500/5' 
                              : 'border-wt-border hover:border-wt-gold/50 hover:bg-wt-gold/5'
                            }`}
                          onClick={() => mediaInputRef.current?.click()}
                        >
                          <input
                            type="file"
                            ref={mediaInputRef}
                            onChange={handleMediaUpload}
                            accept={
                              templateForm.header_type === 'IMAGE' ? 'image/jpeg,image/png,image/webp' :
                              templateForm.header_type === 'VIDEO' ? 'video/mp4,video/3gpp' :
                              'application/pdf,.doc,.docx'
                            }
                            className="hidden"
                          />
                          
                          {uploadingMedia ? (
                            <div className="flex flex-col items-center">
                              <Loader2 className="h-8 w-8 text-wt-gold animate-spin mb-2" />
                              <span className="text-sm text-wt-text-muted">Uploading...</span>
                            </div>
                          ) : templateForm.header_handle ? (
                            <div className="flex flex-col items-center">
                              <CheckCircle className="h-8 w-8 text-green-400 mb-2" />
                              <span className="text-sm text-green-400">Media uploaded</span>
                              <span className="text-xs text-wt-text-muted mt-1 break-all max-w-full">
                                {templateForm.header_handle.length > 50 
                                  ? `${templateForm.header_handle.substring(0, 50)}...` 
                                  : templateForm.header_handle}
                              </span>
                            </div>
                          ) : (
                            <div className="flex flex-col items-center">
                              {templateForm.header_type === 'IMAGE' && <Image className="h-8 w-8 text-wt-text-muted mb-2" />}
                              {templateForm.header_type === 'VIDEO' && <Video className="h-8 w-8 text-wt-text-muted mb-2" />}
                              {templateForm.header_type === 'DOCUMENT' && <File className="h-8 w-8 text-wt-text-muted mb-2" />}
                              <span className="text-sm text-wt-text-primary">Click to upload {templateForm.header_type.toLowerCase()}</span>
                              <span className="text-xs text-wt-text-muted mt-1">
                                {templateForm.header_type === 'IMAGE' && 'JPG, PNG or WebP (max 5MB)'}
                                {templateForm.header_type === 'VIDEO' && 'MP4 or 3GP (max 16MB)'}
                                {templateForm.header_type === 'DOCUMENT' && 'PDF or DOC (max 100MB)'}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                      
                      {templateForm.header_handle && (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                          onClick={() => setTemplateForm({ ...templateForm, header_handle: '' })}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                    
                    {/* Or enter URL manually */}
                    <div className="mt-3">
                      <div className="flex items-center gap-2 text-wt-text-muted text-xs mb-2">
                        <div className="flex-1 h-px bg-wt-border"></div>
                        <span>or enter URL directly</span>
                        <div className="flex-1 h-px bg-wt-border"></div>
                      </div>
                      <Input
                        value={templateForm.header_handle}
                        onChange={(e) => setTemplateForm({ ...templateForm, header_handle: e.target.value })}
                        placeholder={
                          templateForm.header_type === 'IMAGE' ? "https://example.com/sample-image.jpg" :
                          templateForm.header_type === 'VIDEO' ? "https://example.com/sample-video.mp4" :
                          "https://example.com/sample-document.pdf"
                        }
                        className="bg-wt-bg border-wt-border text-wt-text-primary"
                      />
                    </div>
                    
                    <p className="text-xs text-wt-text-muted mt-2">
                      This sample is for Meta to review. You'll provide the actual media URL when sending messages.
                    </p>
                  </div>
                </div>
              )}
              
              {templateForm.header_type === 'LOCATION' && (
                <div className="p-4 bg-blue-500/10 rounded-lg border border-blue-500/30">
                  <p className="text-sm text-blue-400">
                    <strong>Location headers use variables.</strong> Location data will be provided when sending the message.
                    <br />
                    <span className="text-wt-text-muted">Variables: </span>
                    <code className="bg-wt-bg px-2 py-0.5 rounded text-wt-gold">{'{{latitude}}'}</code>,
                    <code className="bg-wt-bg px-2 py-0.5 rounded text-wt-gold ml-1">{'{{longitude}}'}</code>,
                    <code className="bg-wt-bg px-2 py-0.5 rounded text-wt-gold ml-1">{'{{name}}'}</code>,
                    <code className="bg-wt-bg px-2 py-0.5 rounded text-wt-gold ml-1">{'{{address}}'}</code>
                  </p>
                </div>
              )}
            </div>
            
            {/* Body Section */}
            <div className="space-y-3">
              <Label className="text-wt-text-primary text-base font-medium">Message Body *</Label>
              <Textarea
                value={templateForm.body}
                onChange={(e) => handleBodyChange(e.target.value)}
                placeholder="Hello {{1}}, your order #{{2}} has been confirmed! Track at {{3}}"
                className="bg-wt-paper border-wt-border text-wt-text-primary min-h-[120px]"
                maxLength={1024}
              />
              <div className="flex justify-between text-xs text-wt-text-muted">
                <span>Use {'{{1}}'}, {'{{2}}'}, etc. for variables</span>
                <span>{templateForm.body.length}/1024</span>
              </div>
              
              {/* Sample Values for Variables */}
              {detectedVariables.length > 0 && (
                <Card className="bg-wt-paper border-wt-border">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <AlertCircle className="h-4 w-4 text-yellow-400" />
                      <span className="text-sm font-medium text-wt-text-primary">
                        Sample Values Required ({detectedVariables.length} variable{detectedVariables.length > 1 ? 's' : ''})
                      </span>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      {detectedVariables.map((variable, index) => (
                        <div key={variable}>
                          <Label className="text-wt-text-muted text-xs">{variable}</Label>
                          <Input
                            value={templateForm.body_examples[index] || ''}
                            onChange={(e) => handleExampleChange(index, e.target.value)}
                            placeholder={`Sample for ${variable}`}
                            className="bg-wt-bg border-wt-border text-wt-text-primary mt-1"
                          />
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
            
            {/* Footer Section */}
            <div className="space-y-3">
              <Label className="text-wt-text-primary text-base font-medium">Footer (Optional)</Label>
              <Input
                value={templateForm.footer}
                onChange={(e) => setTemplateForm({ ...templateForm, footer: e.target.value })}
                placeholder="e.g., Reply STOP to unsubscribe"
                maxLength={60}
                className="bg-wt-paper border-wt-border text-wt-text-primary"
              />
              <p className="text-xs text-wt-text-muted">Max 60 characters. Good for T&Cs or opt-out info.</p>
            </div>
            
            {/* Carousel Cards Section - Only for Carousel templates */}
            {templateForm.template_type === 'CAROUSEL' && (
              <div className="space-y-4 p-4 bg-wt-bg rounded-lg border border-wt-gold/30">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-wt-gold text-base font-medium">Carousel Cards</Label>
                    <p className="text-xs text-wt-text-muted">Add up to 10 cards. Each card has an image, body text, and buttons.</p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      if (templateForm.carousel_cards.length >= 10) {
                        toast.error('Maximum 10 cards allowed');
                        return;
                      }
                      setTemplateForm({
                        ...templateForm,
                        carousel_cards: [...templateForm.carousel_cards, { header_handle: '', body: '', buttons: [] }]
                      });
                    }}
                    disabled={templateForm.carousel_cards.length >= 10}
                    className="border-wt-gold text-wt-gold hover:bg-wt-gold/10"
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Add Card ({templateForm.carousel_cards.length}/10)
                  </Button>
                </div>
                
                {templateForm.carousel_cards.map((card, cardIndex) => (
                  <div key={cardIndex} className="p-4 bg-wt-paper rounded-lg border border-wt-border space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-wt-text-primary">Card {cardIndex + 1}</span>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          const newCards = templateForm.carousel_cards.filter((_, i) => i !== cardIndex);
                          setTemplateForm({ ...templateForm, carousel_cards: newCards });
                        }}
                        className="text-red-400 hover:text-red-300"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                    
                    {/* Card Image */}
                    <div>
                      <Label className="text-wt-text-muted text-xs">Card Image URL</Label>
                      <Input
                        value={card.header_handle || ''}
                        onChange={(e) => {
                          const newCards = [...templateForm.carousel_cards];
                          newCards[cardIndex].header_handle = e.target.value;
                          setTemplateForm({ ...templateForm, carousel_cards: newCards });
                        }}
                        placeholder="https://example.com/image.jpg"
                        className="bg-wt-bg border-wt-border text-wt-text-primary mt-1"
                      />
                    </div>
                    
                    {/* Card Body */}
                    <div>
                      <Label className="text-wt-text-muted text-xs">Card Body Text (max 160 chars)</Label>
                      <Textarea
                        value={card.body || ''}
                        onChange={(e) => {
                          const newCards = [...templateForm.carousel_cards];
                          newCards[cardIndex].body = e.target.value;
                          setTemplateForm({ ...templateForm, carousel_cards: newCards });
                        }}
                        placeholder="Product description or card content"
                        maxLength={160}
                        rows={2}
                        className="bg-wt-bg border-wt-border text-wt-text-primary mt-1"
                      />
                    </div>
                    
                    {/* Card Buttons */}
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <Label className="text-wt-text-muted text-xs">Card Buttons (max 2)</Label>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            const newCards = [...templateForm.carousel_cards];
                            if (newCards[cardIndex].buttons.length >= 2) {
                              toast.error('Max 2 buttons per card');
                              return;
                            }
                            newCards[cardIndex].buttons.push({ type: 'URL', text: '', url: '' });
                            setTemplateForm({ ...templateForm, carousel_cards: newCards });
                          }}
                          disabled={card.buttons?.length >= 2}
                          className="text-wt-gold"
                        >
                          <Plus className="h-3 w-3 mr-1" />
                          Add Button
                        </Button>
                      </div>
                      {card.buttons?.map((btn, btnIndex) => (
                        <div key={btnIndex} className="flex gap-2 items-center">
                          <Input
                            value={btn.text || ''}
                            onChange={(e) => {
                              const newCards = [...templateForm.carousel_cards];
                              newCards[cardIndex].buttons[btnIndex].text = e.target.value;
                              setTemplateForm({ ...templateForm, carousel_cards: newCards });
                            }}
                            placeholder="Button text"
                            className="bg-wt-bg border-wt-border text-wt-text-primary flex-1"
                          />
                          <Input
                            value={btn.url || ''}
                            onChange={(e) => {
                              const newCards = [...templateForm.carousel_cards];
                              newCards[cardIndex].buttons[btnIndex].url = e.target.value;
                              setTemplateForm({ ...templateForm, carousel_cards: newCards });
                            }}
                            placeholder="https://..."
                            className="bg-wt-bg border-wt-border text-wt-text-primary flex-1"
                          />
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              const newCards = [...templateForm.carousel_cards];
                              newCards[cardIndex].buttons = newCards[cardIndex].buttons.filter((_, i) => i !== btnIndex);
                              setTemplateForm({ ...templateForm, carousel_cards: newCards });
                            }}
                            className="text-red-400"
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
                
                {templateForm.carousel_cards.length === 0 && (
                  <p className="text-center text-wt-text-muted py-4">Click "Add Card" to create carousel cards</p>
                )}
              </div>
            )}
            
            {/* Buttons Section */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-wt-text-primary text-base font-medium">Buttons (Optional)</Label>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={addButton}
                  disabled={templateForm.buttons.length >= 3}
                  className="border-wt-border text-wt-text-muted hover:text-wt-gold"
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Add Button
                </Button>
              </div>
              
              {templateForm.buttons.length === 0 ? (
                <p className="text-sm text-wt-text-muted">No buttons added. Click "Add Button" to add up to 3 buttons.</p>
              ) : (
                <div className="space-y-3">
                  {templateForm.buttons.map((btn, index) => (
                    <Card key={index} className="bg-wt-paper border-wt-border">
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between mb-3">
                          <span className="text-sm font-medium text-wt-text-primary">Button {index + 1}</span>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => removeButton(index)}
                            className="text-red-400 hover:text-red-300 h-6 w-6 p-0"
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                        
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <Label className="text-wt-text-muted text-xs">Button Type</Label>
                            <Select
                              value={btn.type}
                              onValueChange={(value) => updateButton(index, 'type', value)}
                            >
                              <SelectTrigger className="bg-wt-bg border-wt-border text-wt-text-primary mt-1">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent className="bg-wt-surface border-wt-border">
                                {BUTTON_TYPES.map(type => {
                                  const Icon = type.icon;
                                  return (
                                    <SelectItem key={type.value} value={type.value} className="text-wt-text-primary">
                                      <div className="flex items-center gap-2">
                                        <Icon className="h-4 w-4" />
                                        <span>{type.label}</span>
                                      </div>
                                    </SelectItem>
                                  );
                                })}
                              </SelectContent>
                            </Select>
                          </div>
                          
                          {/* Button Text - not shown for COPY_CODE and OTP (Meta uses fixed text) */}
                          {!['COPY_CODE', 'OTP'].includes(btn.type) && (
                            <div>
                              <Label className="text-wt-text-muted text-xs">Button Text</Label>
                              <Input
                                value={btn.text}
                                onChange={(e) => updateButton(index, 'text', e.target.value)}
                                placeholder="e.g., Shop Now"
                                maxLength={25}
                                className="bg-wt-bg border-wt-border text-wt-text-primary mt-1"
                              />
                            </div>
                          )}
                          
                          {/* Fixed text notice for COPY_CODE */}
                          {btn.type === 'COPY_CODE' && (
                            <div className="p-2 bg-blue-500/10 border border-blue-500/20 rounded">
                              <p className="text-xs text-blue-400">
                                Button text is fixed to "Copy offer code" by WhatsApp
                              </p>
                            </div>
                          )}
                          
                          {/* Fixed text notice for OTP */}
                          {btn.type === 'OTP' && (
                            <div className="p-2 bg-blue-500/10 border border-blue-500/20 rounded">
                              <p className="text-xs text-blue-400">
                                Button text is fixed to "Copy code" by WhatsApp
                              </p>
                            </div>
                          )}
                        </div>
                        
                        {btn.type === 'URL' && (
                          <div className="mt-3 space-y-3">
                            {/* URL Type Selection */}
                            <div>
                              <Label className="text-wt-text-muted text-xs">URL Type</Label>
                              <Select
                                value={btn.url_type || 'STATIC'}
                                onValueChange={(v) => {
                                  updateButton(index, 'url_type', v);
                                  // If switching to dynamic, add placeholder to URL if not present
                                  if (v === 'DYNAMIC' && btn.url && !btn.url.includes('{{')) {
                                    updateButton(index, 'url', btn.url + '{{1}}');
                                  }
                                }}
                              >
                                <SelectTrigger className="bg-wt-bg border-wt-border text-wt-text-primary mt-1">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent className="bg-wt-surface border-wt-border">
                                  <SelectItem value="STATIC" className="text-wt-text-primary">
                                    <div className="flex flex-col">
                                      <span>Static</span>
                                      <span className="text-xs text-wt-text-muted">Fixed URL - cannot be changed when sending</span>
                                    </div>
                                  </SelectItem>
                                  <SelectItem value="DYNAMIC" className="text-wt-text-primary">
                                    <div className="flex flex-col">
                                      <span>Dynamic</span>
                                      <span className="text-xs text-wt-text-muted">URL can be customized when sending messages</span>
                                    </div>
                                  </SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            
                            {/* URL Input */}
                            <div>
                              <Label className="text-wt-text-muted text-xs">
                                {btn.url_type === 'DYNAMIC' ? 'Base URL (add {{1}} where dynamic part should be)' : 'Website URL'}
                              </Label>
                              <Input
                                value={btn.url || ''}
                                onChange={(e) => updateButton(index, 'url', e.target.value)}
                                placeholder={btn.url_type === 'DYNAMIC' 
                                  ? "https://example.com/product/{{1}}" 
                                  : "https://example.com/page"
                                }
                                className="bg-wt-bg border-wt-border text-wt-text-primary mt-1"
                              />
                              {btn.url_type === 'DYNAMIC' && (
                                <p className="text-xs text-wt-text-muted mt-1">
                                  Use {"{{1}}"} as placeholder for the dynamic part. Example: https://shop.com/order/{"{{1}}"}
                                </p>
                              )}
                            </div>
                            
                            {/* Sample URL for Dynamic */}
                            {btn.url_type === 'DYNAMIC' && (
                              <div>
                                <Label className="text-wt-text-muted text-xs">Sample URL (required for approval)</Label>
                                <Input
                                  value={btn.url_example || ''}
                                  onChange={(e) => updateButton(index, 'url_example', e.target.value)}
                                  placeholder="https://example.com/product/SKU123"
                                  className="bg-wt-bg border-wt-border text-wt-text-primary mt-1"
                                />
                                <p className="text-xs text-wt-text-muted mt-1">
                                  Provide a real example URL for Meta to review
                                </p>
                              </div>
                            )}
                          </div>
                        )}
                        
                        {btn.type === 'PHONE_NUMBER' && (
                          <div className="mt-3">
                            <Label className="text-wt-text-muted text-xs">Phone Number</Label>
                            <Input
                              value={btn.phone_number || ''}
                              onChange={(e) => updateButton(index, 'phone_number', e.target.value)}
                              placeholder="+971501234567"
                              className="bg-wt-bg border-wt-border text-wt-text-primary mt-1"
                            />
                          </div>
                        )}
                        
                        {btn.type === 'COPY_CODE' && (
                          <div className="mt-3">
                            <Label className="text-wt-text-muted text-xs">Sample Code</Label>
                            <Input
                              value={btn.code_example || ''}
                              onChange={(e) => updateButton(index, 'code_example', e.target.value)}
                              placeholder="SAVE20"
                              className="bg-wt-bg border-wt-border text-wt-text-primary mt-1"
                            />
                          </div>
                        )}
                        
                        {btn.type === 'OTP' && (
                          <div className="mt-3 p-3 bg-blue-500/10 border border-blue-500/30 rounded">
                            <p className="text-xs text-blue-400">
                              <strong>OTP Button:</strong> This button auto-fills OTP codes for authentication templates.
                              The button text is fixed to "Copy code" by WhatsApp.
                            </p>
                            <div className="mt-2">
                              <Label className="text-wt-text-muted text-xs">OTP Type</Label>
                              <Select
                                value={btn.otp_type || 'COPY_CODE'}
                                onValueChange={(value) => updateButton(index, 'otp_type', value)}
                              >
                                <SelectTrigger className="bg-wt-bg border-wt-border text-wt-text-primary mt-1">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent className="bg-wt-surface border-wt-border">
                                  <SelectItem value="COPY_CODE" className="text-wt-text-primary">Copy Code</SelectItem>
                                  <SelectItem value="ONE_TAP" className="text-wt-text-primary">One-Tap Autofill</SelectItem>
                                  <SelectItem value="ZERO_TAP" className="text-wt-text-primary">Zero-Tap (Auto-fill)</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                        )}
                        
                        {btn.type === 'FLOW' && (
                          <div className="mt-3">
                            <Label className="text-wt-text-muted text-xs">Flow ID</Label>
                            <Input
                              value={btn.flow_id || ''}
                              onChange={(e) => updateButton(index, 'flow_id', e.target.value)}
                              placeholder="Enter WhatsApp Flow ID"
                              className="bg-wt-bg border-wt-border text-wt-text-primary mt-1"
                            />
                            <p className="text-xs text-wt-text-muted mt-1">
                              Create Flows in Meta Business Suite → WhatsApp → Flows
                            </p>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>
            
            {/* Authentication Options */}
            {templateForm.category === 'AUTHENTICATION' && (
              <div className="space-y-3 p-4 bg-wt-paper rounded-lg border border-wt-border">
                <Label className="text-wt-text-primary text-base font-medium">Authentication Options</Label>
                <div className="flex items-center gap-3">
                  <Checkbox
                    checked={templateForm.add_security_recommendation}
                    onCheckedChange={(checked) => setTemplateForm({ ...templateForm, add_security_recommendation: checked })}
                    className="border-wt-border"
                  />
                  <span className="text-sm text-wt-text-primary">Add security recommendation text</span>
                </div>
                <div>
                  <Label className="text-wt-text-muted text-xs">Code Expiration (minutes)</Label>
                  <Input
                    type="number"
                    value={templateForm.code_expiration_minutes || ''}
                    onChange={(e) => setTemplateForm({ ...templateForm, code_expiration_minutes: parseInt(e.target.value) || null })}
                    placeholder="e.g., 5"
                    className="bg-wt-bg border-wt-border text-wt-text-primary mt-1 w-32"
                  />
                </div>
              </div>
            )}
            
            {/* Preview */}
            <div className="space-y-3">
              <Label className="text-wt-text-primary text-base font-medium">Preview</Label>
              <div className="bg-[#0b141a] rounded-lg p-4 max-w-sm">
                <div className="bg-[#005c4b] rounded-lg p-3 text-white text-sm">
                  {templateForm.header_type === 'TEXT' && templateForm.header_text && (
                    <p className="font-bold mb-2">{templateForm.header_text}</p>
                  )}
                  {['IMAGE', 'VIDEO', 'DOCUMENT'].includes(templateForm.header_type) && (
                    <div className="bg-black/30 rounded h-32 mb-2 flex flex-col items-center justify-center">
                      {templateForm.header_type === 'IMAGE' && <Image className="h-8 w-8 text-white/40 mb-1" />}
                      {templateForm.header_type === 'VIDEO' && <Video className="h-8 w-8 text-white/40 mb-1" />}
                      {templateForm.header_type === 'DOCUMENT' && <File className="h-8 w-8 text-white/40 mb-1" />}
                      <span className="text-white/60 text-xs">[{templateForm.header_type}]</span>
                      <span className="text-white/40 text-xs mt-1">{'{{header_media}}'}</span>
                    </div>
                  )}
                  {templateForm.header_type === 'LOCATION' && (
                    <div className="bg-black/30 rounded h-32 mb-2 flex flex-col items-center justify-center">
                      <MapPin className="h-8 w-8 text-white/40 mb-1" />
                      <span className="text-white/60 text-xs">[LOCATION]</span>
                      <span className="text-white/40 text-xs mt-1">{'{{latitude}}, {{longitude}}'}</span>
                    </div>
                  )}
                  <p className="whitespace-pre-wrap">
                    {templateForm.body || 'Your message body will appear here...'}
                  </p>
                  {templateForm.footer && (
                    <p className="text-white/60 text-xs mt-2">{templateForm.footer}</p>
                  )}
                </div>
                {templateForm.buttons.length > 0 && (
                  <div className="mt-1 space-y-1">
                    {templateForm.buttons.map((btn, idx) => (
                      <div key={idx} className="bg-[#005c4b]/50 rounded py-2 text-center text-white/90 text-sm">
                        {btn.text || `Button ${idx + 1}`}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
          
          <DialogFooter className="border-t border-wt-border pt-4">
            <Button
              variant="outline"
              onClick={() => { setShowTemplateDialog(false); setEditingTemplate(null); }}
              className="border-wt-border text-wt-text-muted"
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateTemplate}
              disabled={saving || !templateForm.name || !templateForm.body}
              className="bg-wt-gold hover:bg-wt-gold-dark text-black"
              data-testid="wa-template-submit-btn"
            >
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Submitting...
                </>
              ) : (
                editingTemplate ? 'Save Edit' : 'Submit for Approval'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Test Message Dialog - Enhanced */}
      <Dialog open={showTestDialog} onOpenChange={(open) => {
        setShowTestDialog(open);
        if (!open) resetTestDialog();
      }}>
        <DialogContent className="bg-wt-surface border-wt-border max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-wt-text-primary">Send Test Message</DialogTitle>
          </DialogHeader>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 py-4">
            {/* Left Column - Input Fields */}
            <div className="space-y-4">
              {/* Phone Number */}
              <div>
                <Label className="text-wt-text-primary">Phone Number *</Label>
                <Input
                  value={testPhone}
                  onChange={(e) => setTestPhone(e.target.value)}
                  placeholder="e.g., 971501234567"
                  className="bg-wt-paper border-wt-border text-wt-text-primary mt-1"
                />
                <p className="text-xs text-wt-text-muted mt-1">Include country code without + or spaces</p>
              </div>
              
              {/* Template Selection */}
              <div>
                <Label className="text-wt-text-primary">Template *</Label>
                <Select value={testTemplate} onValueChange={handleTestTemplateChange}>
                  <SelectTrigger className="bg-wt-paper border-wt-border text-wt-text-primary mt-1">
                    <SelectValue placeholder="Select a template" />
                  </SelectTrigger>
                  <SelectContent className="bg-wt-surface border-wt-border max-h-60">
                    {templates.filter(t => t.status === 'APPROVED').map((template) => (
                      <SelectItem key={template.name} value={template.name}>
                        <div className="flex items-center gap-2">
                          <span>{template.name}</span>
                          {template.components?.some(c => c.type === 'HEADER' && c.format === 'IMAGE') && (
                            <Image className="h-3 w-3 text-wt-text-muted" />
                          )}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Dynamic Variable Inputs based on selected template */}
              {selectedTestTemplate && (() => {
                const requirements = parseTemplateRequirements(selectedTestTemplate);
                return (
                  <>
                    {/* Header Media URL */}
                    {(requirements.hasHeaderImage || requirements.hasHeaderVideo || requirements.hasHeaderDocument) && (
                      <div>
                        <Label className="text-wt-text-primary flex items-center gap-2">
                          {requirements.hasHeaderImage && <Image className="h-4 w-4" />}
                          {requirements.hasHeaderVideo && <Video className="h-4 w-4" />}
                          {requirements.hasHeaderDocument && <File className="h-4 w-4" />}
                          Header Media URL *
                        </Label>
                        <Input
                          value={testHeaderImageUrl}
                          onChange={(e) => setTestHeaderImageUrl(e.target.value)}
                          placeholder={requirements.hasHeaderImage ? "https://example.com/image.jpg" : 
                                       requirements.hasHeaderVideo ? "https://example.com/video.mp4" : 
                                       "https://example.com/document.pdf"}
                          className="bg-wt-paper border-wt-border text-wt-text-primary mt-1"
                        />
                        <p className="text-xs text-wt-text-muted mt-1">
                          {requirements.hasHeaderImage ? "Provide a publicly accessible image URL (JPG, PNG)" :
                           requirements.hasHeaderVideo ? "Provide a publicly accessible video URL (MP4)" :
                           "Provide a publicly accessible document URL (PDF)"}
                        </p>
                      </div>
                    )}
                    
                    {/* Body Variables */}
                    {requirements.bodyParams.length > 0 && (
                      <div className="space-y-3">
                        <Label className="text-wt-text-primary flex items-center gap-2">
                          <MessageSquare className="h-4 w-4" />
                          Body Variables ({requirements.bodyParams.length})
                        </Label>
                        {requirements.bodyParams.map((param) => (
                          <div key={param.index}>
                            <div className="flex items-center gap-2 mb-1">
                              <Badge variant="outline" className="text-xs">{param.placeholder}</Badge>
                              {param.example && (
                                <span className="text-xs text-wt-text-muted">e.g., {param.example}</span>
                              )}
                            </div>
                            <Input
                              value={testVariables[param.index] || ''}
                              onChange={(e) => setTestVariables({...testVariables, [param.index]: e.target.value})}
                              placeholder={param.example || `Variable ${param.index}`}
                              className="bg-wt-paper border-wt-border text-wt-text-primary"
                            />
                          </div>
                        ))}
                      </div>
                    )}
                    
                    {/* URL Button Parameters */}
                    {requirements.urlButtons.length > 0 && (
                      <div className="space-y-3">
                        <Label className="text-wt-text-primary flex items-center gap-2">
                          <Link className="h-4 w-4" />
                          Button URL Parameters
                        </Label>
                        {requirements.urlButtons.map((btn) => (
                          <div key={btn.index}>
                            <p className="text-xs text-wt-text-muted mb-1">
                              Button: {btn.text}
                            </p>
                            <Input
                              value={testButtonParams[btn.index] || ''}
                              onChange={(e) => setTestButtonParams({...testButtonParams, [btn.index]: e.target.value})}
                              placeholder="Dynamic URL parameter"
                              className="bg-wt-paper border-wt-border text-wt-text-primary"
                            />
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
            
            {/* Right Column - Preview & Status */}
            <div className="space-y-4">
              {/* Message Preview */}
              <div>
                <Label className="text-wt-text-primary flex items-center gap-2 mb-2">
                  <Eye className="h-4 w-4" />
                  Message Preview
                </Label>
                <div className="bg-wt-bg rounded-lg p-4 border border-wt-border min-h-[200px]">
                  {selectedTestTemplate ? (
                    <div className="space-y-3">
                      {/* Header Preview */}
                      {(() => {
                        const requirements = parseTemplateRequirements(selectedTestTemplate);
                        return (requirements.hasHeaderImage || requirements.hasHeaderVideo || requirements.hasHeaderDocument) && (
                          <div className="bg-wt-surface rounded-lg p-3 border border-wt-border">
                            {testHeaderImageUrl ? (
                              requirements.hasHeaderImage ? (
                                <img 
                                  src={testHeaderImageUrl} 
                                  alt="Header" 
                                  className="w-full h-32 object-cover rounded"
                                  onError={(e) => e.target.style.display = 'none'}
                                />
                              ) : (
                                <div className="flex items-center gap-2 text-wt-text-muted">
                                  {requirements.hasHeaderVideo ? <Video className="h-8 w-8" /> : <File className="h-8 w-8" />}
                                  <span className="text-sm truncate">{testHeaderImageUrl}</span>
                                </div>
                              )
                            ) : (
                              <div className="flex items-center justify-center h-32 text-wt-text-muted">
                                {requirements.hasHeaderImage && <Image className="h-12 w-12 opacity-30" />}
                                {requirements.hasHeaderVideo && <Video className="h-12 w-12 opacity-30" />}
                                {requirements.hasHeaderDocument && <File className="h-12 w-12 opacity-30" />}
                              </div>
                            )}
                          </div>
                        );
                      })()}
                      
                      {/* Body Preview */}
                      <div className="text-sm text-wt-text-primary whitespace-pre-wrap">
                        {generatePreviewText() || (
                          <span className="text-wt-text-muted italic">Select a template to see preview</span>
                        )}
                      </div>
                      
                      {/* Footer Preview */}
                      {selectedTestTemplate.components?.find(c => c.type === 'FOOTER')?.text && (
                        <p className="text-xs text-wt-text-muted">
                          {selectedTestTemplate.components.find(c => c.type === 'FOOTER').text}
                        </p>
                      )}
                      
                      {/* Buttons Preview */}
                      {selectedTestTemplate.components?.find(c => c.type === 'BUTTONS')?.buttons?.length > 0 && (
                        <div className="flex flex-wrap gap-2 pt-2 border-t border-wt-border">
                          {selectedTestTemplate.components.find(c => c.type === 'BUTTONS').buttons.map((btn, idx) => (
                            <Badge 
                              key={idx} 
                              variant="outline" 
                              className="text-xs cursor-pointer hover:bg-wt-surface"
                            >
                              {btn.type === 'URL' && <ExternalLink className="h-3 w-3 mr-1" />}
                              {btn.type === 'PHONE_NUMBER' && <Phone className="h-3 w-3 mr-1" />}
                              {btn.text}
                            </Badge>
                          ))}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="flex items-center justify-center h-full text-wt-text-muted">
                      <p className="text-sm">Select a template to see preview</p>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Message Status */}
              {testMessageStatus && (
                <div className={`rounded-lg p-4 border ${
                  testMessageStatus === 'sending' ? 'bg-blue-500/10 border-blue-500/30' :
                  testMessageStatus === 'sent' ? 'bg-green-500/10 border-green-500/30' :
                  testMessageStatus === 'delivered' ? 'bg-green-500/10 border-green-500/30' :
                  testMessageStatus === 'read' ? 'bg-green-500/10 border-green-500/30' :
                  'bg-red-500/10 border-red-500/30'
                }`}>
                  <div className="flex items-center gap-2">
                    {testMessageStatus === 'sending' && <Loader2 className="h-5 w-5 animate-spin text-blue-400" />}
                    {testMessageStatus === 'sent' && <CheckCircle className="h-5 w-5 text-green-400" />}
                    {testMessageStatus === 'delivered' && <CheckCircle className="h-5 w-5 text-green-400" />}
                    {testMessageStatus === 'read' && <CheckCircle className="h-5 w-5 text-green-400" />}
                    {testMessageStatus === 'failed' && <XCircle className="h-5 w-5 text-red-400" />}
                    <div>
                      <p className={`font-medium ${
                        testMessageStatus === 'failed' ? 'text-red-400' : 
                        testMessageStatus === 'sending' ? 'text-blue-400' : 'text-green-400'
                      }`}>
                        {testMessageStatus === 'sending' ? 'Sending message...' :
                         testMessageStatus === 'sent' ? 'Message Sent!' :
                         testMessageStatus === 'delivered' ? 'Message Delivered' :
                         testMessageStatus === 'read' ? 'Message Read' :
                         'Message Failed'}
                      </p>
                      {testMessageError && (
                        <p className="text-xs text-red-400 mt-1">{testMessageError}</p>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
          
          <DialogFooter className="flex justify-between items-center">
            <div className="text-xs text-wt-text-muted">
              {selectedTestTemplate && (() => {
                const req = parseTemplateRequirements(selectedTestTemplate);
                return `${req.bodyParams.length} variable(s) • ${req.hasHeaderImage ? 'Image header' : req.hasHeaderVideo ? 'Video header' : req.hasHeaderDocument ? 'Document header' : 'No header'}`;
              })()}
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  setShowTestDialog(false);
                  resetTestDialog();
                }}
                className="border-wt-border text-wt-text-muted"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSendTestMessage}
                disabled={sendingTest || !testPhone || !testTemplate}
                className="bg-green-600 hover:bg-green-700 text-white"
              >
                {sendingTest ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4 mr-2" />
                    Send Test
                  </>
                )}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Template Dialog */}
      <Dialog open={showViewDialog} onOpenChange={setShowViewDialog}>
        <DialogContent className="bg-wt-surface border-wt-border max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-wt-text-primary flex items-center gap-3">
              <span>Template Details</span>
              {viewingTemplate && getStatusBadge(viewingTemplate.status)}
            </DialogTitle>
          </DialogHeader>
          
          {viewingTemplate && (
            <div className="space-y-4 py-2">
              {/* Basic Info */}
              <div className="grid grid-cols-3 gap-4 p-4 bg-wt-bg rounded-lg">
                <div>
                  <p className="text-xs text-wt-text-muted">Name</p>
                  <p className="text-sm font-medium text-wt-text-primary">{viewingTemplate.name}</p>
                </div>
                <div>
                  <p className="text-xs text-wt-text-muted">Category</p>
                  <p className="text-sm text-wt-text-primary">{viewingTemplate.category}</p>
                </div>
                <div>
                  <p className="text-xs text-wt-text-muted">Language</p>
                  <p className="text-sm text-wt-text-primary">{viewingTemplate.language}</p>
                </div>
              </div>

              {/* Components */}
              <div className="space-y-3">
                <h4 className="text-sm font-medium text-wt-text-primary">Template Components</h4>
                
                {viewingTemplate.components?.map((comp, idx) => (
                  <div key={idx} className="p-3 bg-wt-paper rounded-lg border border-wt-border">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-xs font-medium px-2 py-0.5 bg-wt-gold/20 text-wt-gold rounded">
                        {comp.type}
                      </span>
                      {comp.format && (
                        <span className="text-xs text-wt-text-muted">({comp.format})</span>
                      )}
                    </div>
                    
                    {comp.type === 'HEADER' && comp.text && (
                      <p className="text-sm text-wt-text-primary">{comp.text}</p>
                    )}
                    
                    {comp.type === 'BODY' && (
                      <p className="text-sm text-wt-text-primary whitespace-pre-wrap">{comp.text}</p>
                    )}
                    
                    {comp.type === 'FOOTER' && (
                      <p className="text-sm text-wt-text-muted">{comp.text}</p>
                    )}
                    
                    {comp.type === 'BUTTONS' && comp.buttons && (
                      <div className="space-y-2">
                        {comp.buttons.map((btn, btnIdx) => (
                          <div key={btnIdx} className="flex items-center gap-3 p-2 bg-wt-bg rounded">
                            <span className="text-xs px-2 py-0.5 bg-blue-500/20 text-blue-400 rounded">
                              {btn.type}
                            </span>
                            <span className="text-sm text-wt-text-primary">{btn.text}</span>
                            {btn.url && (
                              <span className="text-xs text-wt-text-muted truncate max-w-[200px]">{btn.url}</span>
                            )}
                            {btn.phone_number && (
                              <span className="text-xs text-wt-text-muted">{btn.phone_number}</span>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Rejection Reason */}
              {viewingTemplate.status === 'REJECTED' && viewingTemplate.rejected_reason && (
                <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                  <p className="text-xs font-medium text-red-400 mb-1">Rejection Reason</p>
                  <p className="text-sm text-red-300">{viewingTemplate.rejected_reason}</p>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowViewDialog(false)}
              className="border-wt-border"
            >
              Close
            </Button>
            {viewingTemplate && (viewingTemplate.status === 'REJECTED' || viewingTemplate.status === 'DRAFT') && (
              <Button
                onClick={() => {
                  setShowViewDialog(false);
                  // Trigger edit
                  const body = viewingTemplate.components?.find(c => c.type === 'BODY')?.text || '';
                  const header = viewingTemplate.components?.find(c => c.type === 'HEADER');
                  const footer = viewingTemplate.components?.find(c => c.type === 'FOOTER')?.text || '';
                  const buttons = viewingTemplate.components?.find(c => c.type === 'BUTTONS')?.buttons || [];
                  
                  setTemplateForm({
                    name: viewingTemplate.name,
                    category: viewingTemplate.category,
                    language: viewingTemplate.language,
                    header_type: header?.format || 'NONE',
                    header_text: header?.format === 'TEXT' ? header?.text : '',
                    header_handle: '',
                    body: body,
                    body_examples: [],
                    footer: footer,
                    buttons: buttons.map(btn => ({
                      type: btn.type,
                      text: btn.text,
                      url: btn.url,
                      url_type: btn.url?.includes('{{') ? 'DYNAMIC' : 'STATIC',
                      phone_number: btn.phone_number
                    })),
                    add_security_recommendation: false,
                    code_expiration_minutes: null
                  });
                  setEditingTemplate(viewingTemplate);
                  setShowTemplateDialog(true);
                }}
                className="bg-wt-gold hover:bg-wt-gold-hover text-black"
              >
                <Edit2 className="h-4 w-4 mr-2" />
                Edit & Resubmit
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Register Phone Dialog */}
      <Dialog open={showRegisterDialog} onOpenChange={setShowRegisterDialog}>
        <DialogContent className="bg-wt-surface border-wt-border max-w-md">
          <DialogHeader>
            <DialogTitle className="text-wt-text-primary">Register Phone Number</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
              <p className="text-sm text-yellow-400">
                <strong>Important:</strong> Before registering, make sure you have:
              </p>
              <ol className="text-sm text-yellow-400/80 mt-2 list-decimal list-inside space-y-1">
                <li>Completed OTP verification in Meta Business Suite</li>
                <li>Your phone number shows in WhatsApp Manager</li>
              </ol>
            </div>
            
            <div>
              <Label className="text-wt-text-primary">6-Digit PIN</Label>
              <Input
                type="text"
                value={registerPin}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                  setRegisterPin(value);
                }}
                placeholder="Enter 6-digit PIN (e.g., 123456)"
                className="bg-wt-paper border-wt-border text-wt-text-primary mt-1 text-center text-2xl tracking-widest"
                maxLength={6}
              />
              <p className="text-xs text-wt-text-muted mt-2">
                This PIN will be used for two-factor authentication when managing your WhatsApp Business API.
                Choose a PIN you can remember.
              </p>
            </div>
          </div>
          
          <DialogFooter className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setShowRegisterDialog(false);
                setRegisterPin('');
              }}
              className="border-wt-border"
            >
              Cancel
            </Button>
            <Button
              onClick={handleRegisterPhone}
              disabled={registering || registerPin.length !== 6}
              className="bg-wt-gold hover:bg-wt-gold-dark text-black"
            >
              {registering ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Registering...
                </>
              ) : (
                <>
                  <Phone className="h-4 w-4 mr-2" />
                  Register Phone
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Remove Integration Dialog */}
      <Dialog open={showRemoveDialog} onOpenChange={setShowRemoveDialog}>
        <DialogContent className="bg-wt-surface border-wt-border max-w-md">
          <DialogHeader>
            <DialogTitle className="text-wt-text-primary">Remove WhatsApp Integration</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
              <p className="text-sm text-red-400">
                <strong>Warning:</strong> This will remove all WhatsApp configuration for your company, including:
              </p>
              <ul className="text-sm text-red-400/80 mt-2 list-disc list-inside space-y-1">
                <li>Phone Number ID and Access Token</li>
                <li>Webhook configuration</li>
                <li>API credentials</li>
              </ul>
              <p className="text-sm text-red-400/80 mt-2">
                Note: Templates and conversation history will be preserved.
              </p>
            </div>
          </div>
          
          <DialogFooter className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => setShowRemoveDialog(false)}
              className="border-wt-border"
            >
              Cancel
            </Button>
            <Button
              onClick={handleRemoveIntegration}
              disabled={removing}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {removing ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Removing...
                </>
              ) : (
                <>
                  <Trash2 className="h-4 w-4 mr-2" />
                  Remove Integration
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default WhatsAppIntegrationPage;
